"use client";

import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { LinkArrowIcon } from "@/shared/components/LinkArrowIcon";
import { cn } from "@/lib/utils";

interface SearchPaginationProps {
  total: number;
  current: number;
}

function formatPageNumber(page: number): string {
  return String(page).padStart(2, "0");
}

/** Figma search pagination: 01 02 03 … N → */
export function SearchPagination({ total, current }: SearchPaginationProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  if (total < 2) return null;

  const makeLink = (page: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", String(page));
    return `${pathname}?${params.toString()}`;
  };

  const pages: Array<number | "ellipsis"> = [];

  if (total <= 5) {
    for (let page = 1; page <= total; page += 1) {
      pages.push(page);
    }
  } else if (current <= 3) {
    pages.push(1, 2, 3, "ellipsis", total);
  } else if (current >= total - 2) {
    pages.push(1, "ellipsis", total - 2, total - 1, total);
  } else {
    pages.push(1, "ellipsis", current, "ellipsis", total);
  }

  return (
    <nav aria-label="Пагинация результатов поиска" className="flex items-center gap-4">
      {pages.map((page, index) => {
        if (page === "ellipsis") {
          return (
            <span
              key={`ellipsis-${index}`}
              className="font-sans text-base leading-[1.2] text-tag-foreground"
            >
              ...
            </span>
          );
        }

        const isActive = page === current;

        return (
          <Link
            key={page}
            href={makeLink(page)}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "min-w-[22px] text-center font-sans text-base leading-[1.2] transition-colors",
              isActive
                ? "text-foreground underline decoration-solid underline-offset-4"
                : "text-tag-foreground hover:text-foreground"
            )}
          >
            {formatPageNumber(page)}
          </Link>
        );
      })}

      {current < total && (
        <Link
          href={makeLink(current + 1)}
          className="inline-flex items-center text-foreground transition-colors hover:text-primary"
          aria-label="Следующая страница"
        >
          <LinkArrowIcon />
        </Link>
      )}
    </nav>
  );
}
